import { useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

/** card widths: a swipeable 85% card on a phone, two across, then three */
export const SLIDE_WIDTH = "w-[85%] shrink-0 snap-start sm:w-[calc((100%-1rem)/2)] lg:w-[calc((100%-2rem)/3)]";

/**
 * The scrolling row and its previous / next buttons, shared by every slider
 * on the country guides — packages, centres and patient stories. The
 * buttons step one card at a time, measured from the first child marked
 * data-card, so the step is right at any width. They show on a phone too —
 * smaller and tucked further in so they stay on screen — and the row can
 * still be swiped. With `byPage` they step a whole view instead: every card
 * in view moves out together (three on a desktop, one on a phone).
 */
export function SliderRow({ children, label, byPage = false }: { children: ReactNode; label: string; byPage?: boolean }) {
  const track = useRef<HTMLDivElement>(null);

  const step = (dir: 1 | -1) => {
    const el = track.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-card]");
    // a view is the row's width plus the 16px gap before the next card
    const by = byPage ? el.clientWidth + 16 : card ? card.getBoundingClientRect().width + 16 : el.clientWidth;
    el.scrollBy({ left: dir * by, behavior: "smooth" });
  };

  const arrow =
    "absolute top-[38%] z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-[#2C4E5A] text-white shadow-lg transition-colors hover:bg-[#24404A] sm:h-11 sm:w-11";

  return (
    <div className="relative">
      <button type="button" onClick={() => step(-1)} aria-label={`Previous ${label}`} className={`${arrow} -left-2 sm:-left-4`}>
        <ChevronLeft className="h-5 w-5" />
      </button>
      <div
        ref={track}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
      <button type="button" onClick={() => step(1)} aria-label={`Next ${label}`} className={`${arrow} -right-2 sm:-right-4`}>
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
}

export type SliderCard = {
  to: string;
  img: string;
  /** the small copper label with a dot — a place, or a kind of programme */
  label: string;
  /** the cream pill beside it */
  tag: string;
  title: string;
  desc: string;
};

/**
 * A row of photo cards — the packages and centres sliders, both carrying the
 * same cards the Geo city pages do.
 */
export default function CardSlider({
  id,
  heading,
  intro,
  cards,
  footer,
}: {
  id: string;
  heading: ReactNode;
  intro: ReactNode;
  cards: SliderCard[];
  footer?: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 pt-12">
      <h2 className="mb-1 text-xl font-bold text-[#2C4E5A] md:text-2xl">{heading}</h2>
      <p className="mb-5 text-sm text-foreground">{intro}</p>

      <SliderRow label="cards">
        {cards.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            data-card
            className={`group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-white shadow-sm transition-shadow hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C4E5A] ${SLIDE_WIDTH}`}
          >
            <img src={c.img} alt={c.title} loading="lazy" decoding="async" className="aspect-[16/9] w-full object-cover" />
            <span className="flex flex-1 flex-col p-5 md:p-6">
              {/* Below md the card is too narrow for the label and the pill on
                  one line, and it was wrapping in some cards and not others, so
                  titles started at different heights. Stacked there, side by
                  side from md up, the block is the same shape in every card. */}
              <span className="mb-3 flex flex-col items-start gap-1.5 md:flex-row md:items-center md:gap-2">
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-[#9A5F3A]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C48257]" aria-hidden="true" />
                  {c.label}
                </span>
                <span className="whitespace-nowrap rounded-full bg-[#EDE8D0] px-3 py-1 text-xs font-semibold text-[#325A67]">
                  {c.tag}
                </span>
              </span>
              <span className="mb-2 block text-lg font-bold text-[#2C4E5A] md:text-xl">{c.title}</span>
              <span className="block text-sm leading-relaxed md:text-base">{c.desc}</span>
              <span className="mt-auto pt-4 text-sm font-bold text-[#2C4E5A]">
                Learn more <span aria-hidden="true">&rarr;</span>
              </span>
            </span>
          </Link>
        ))}
      </SliderRow>

      {footer}
    </section>
  );
}
